import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { PageAccent } from "../components/PageAccent";
import { FadeUp } from "../components/Reveal";
import { RecurringGiftsCard } from "../components/RecurringGiftsCard";
import { EmSwash } from "../components/EmSwash";
import { useNoIndex } from "../lib/useNoIndex";

/**
 * RecurringGifts — full manager page for returning donors.
 *
 * This is the "why sign in" surface. Only donors can create gifts
 * (backend guards this with role=donor). Students / anonymous get
 * bounced to /login with a note.
 */
export default function RecurringGifts() {
  useNoIndex();
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login", { replace: true, state: { from: "/recurring-gifts" } });
    }
  }, [loading, user, navigate]);

  if (loading || !user) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sand">
        <Loader2 className="h-6 w-6 animate-spin text-olive" />
      </main>
    );
  }

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const canCreate = user.role === "donor";

  return (
    <main data-testid="recurring-gifts-page" className="bg-sand">
      <Navbar
        onStart={handleLogout}
        primaryLabel="Sign out"
        secondaryLabel={null}
      />

      <section className="mx-auto max-w-5xl px-6 pb-16 pt-32 lg:px-8 lg:pt-44">
        <FadeUp tier="sm">
          <PageAccent className="mb-5" />
        </FadeUp>
        <FadeUp>
          <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">
            Your recurring gifts
          </span>
          <h1 className="mt-4 max-w-2xl font-serif text-3xl lg:text-[3.5rem] leading-[0.95] tracking-tight text-ink">
            Steady help, on <EmSwash>your terms.</EmSwash>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-olive">
            Change your amount, switch cadence, pause when life happens, cancel any time. This is the gentlest kind of generosity — small and reliable.
          </p>
        </FadeUp>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-24 lg:px-8 lg:pb-36">
        {!canCreate && (
          <div className="mb-8 rounded-md border border-border bg-bone p-4 text-[13px] text-olive" data-testid="rg-donor-only-notice">
            You're signed in as a <strong className="text-ink">{user.role}</strong>. Recurring gifts are managed by donors — but you can still see the interface here.
          </div>
        )}
        <RecurringGiftsCard canCreate={canCreate} />
      </section>

      <Footer
        onStart={handleLogout}
        onDonate={() => navigate("/dashboard")}
        primaryLabel="Sign out"
        secondaryLabel="Back to dashboard"
      />
    </main>
  );
}
